import { ArrowDown, ArrowRight, ArrowUp, Check, Clock, ExternalLink } from 'react-feather';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';

import { Locale } from '../../__generated__/graphql';

import { Container } from '../Common/Layout';
import { EmbedContext } from '../Common/EmbedProvider';
import { EventHeader, EventInfo } from '../../common/Common/EventHeader';
import { Theme } from '../../theme';
import { defaultSecondary } from '../../theme/variables';
import { getUrlWithoutPrefix, isEmpty, resizedImageUrl } from '../../common/helpers';
import { useEvent } from '../Common/EventProvider';
import { useTracking } from '../../common/Tracking';
import SupportaSymbol from '../../common/Common/SupportaSymbol';
import UI from '../../common/UI';
import config from '../config';
import useCheckoutUrlState, { CheckoutUrlState } from '../Checkout/useCheckoutUrlState';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useStickyElement from '../../common/useStickyElement';

const AboutPage = () => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const { embedded, scrollToTop } = useContext(EmbedContext);
  const [urlState] = useCheckoutUrlState();
  const device = useMediaDevice();
  const { track } = useTracking();

  const { event } = useEvent();

  useDocumentTitle([event.title]);

  const hasTickets = event.ticket_categories.some((ticketCategory) => (
    ticketCategory.tickets_for_sale.length > 0
  ));

  const usps = [event.usp_1, event.usp_2, event.usp_3].filter((usp) => usp);

  const eventsPerPage = 3;
  const [eventsLimit, setEventsLimit] = useState(eventsPerPage);
  const visibleEvents = event.related_events.slice(0, eventsLimit);

  const { anchorRef, elementRef, isSticky, position } = useStickyElement({
    scrollContainerId: embedded && !device.isSmall ? 'PageContainer' : undefined,
    offset: -46,
    hideOnScrollEnd: true,
  });

  const theme = useTheme() as Theme;

  const containsTicketsUrlParam = [
    'paymentStatus',
    'showCategories',
    'ticketCategoryIds',
    'coupon',
    'invitationCode',
    'charityId',
  ].some((param) => !isEmpty(urlState[param as keyof CheckoutUrlState]));

  useEffect(() => {
    track?.('EventPage:Opened');
  }, [track]);

  if (containsTicketsUrlParam) {
    return <Redirect to={`/${event.id}/tickets${window.location.search}`} />;
  }

  return (
    <>
      {(event.registration_opens_at || hasTickets || event.enable_interest_list) && (
        <UI.Div ref={anchorRef}>
          <UI.Div
            ref={elementRef}
            style={{
              bottom: isSticky ? (embedded && !device.isSmall ? 2.5 : 0.75) * theme.gutter : undefined,
              left: isSticky ? position.left : undefined,
              right: isSticky ? position.right : undefined,
              position: isSticky ? 'fixed' : 'relative',
              zIndex: 300,
            }}
          >
            <Container sc={{ py: 0, px: isSticky ? undefined : 0 }}>
              <UI.FadeIn sc={{ when: isSticky }}>
                <RegisterButton
                  to={`/${event.id}/${(hasTickets || event.registration_opens_at) ? 'tickets' : 'interest'}`}
                  sc={{
                    brand: isSticky ? 'secondary/90' : 'secondary',
                    size: 'xl',
                    block: true,
                  }}
                  onClick={() => scrollToTop()}
                >
                  {event.registration_opens_at ? (
                    <>
                      <UI.Icon>
                        <Clock />
                      </UI.Icon>
                      {' '}
                      {t('event_info.registration_opens_at', {
                        time: formatDate(
                          parseDate(event.registration_opens_at, { timezone: 'UTC' }),
                          // Assumes the timestamp is only present if registration opens in < 1 hour.
                          { format: 'display_time', timezone: null },
                        ),
                      })}
                    </>
                  ) : (
                    <>
                      {hasTickets ? t('event_info.register') : t('interest_list.newsletter')}
                      {' '}
                      <UI.Icon>
                        <ArrowRight />
                      </UI.Icon>
                    </>
                  )}
                </RegisterButton>
              </UI.FadeIn>
            </Container>
          </UI.Div>
        </UI.Div>
      )}

      {usps.length > 0 && (
        <UI.Card sc={{ outline: 'gray.200', py: [2, embedded ? 3 : 4], px: [2, embedded ? 3 : 7], boxShadow: 'xs' }}>
          <UI.GridContainer sc={{ columns: ['1fr', usps.map(() => '1fr').join(' 1px ')], gutter: 0.5 }}>
            {usps.map((usp, index) => (
              <Fragment key={index}>
                {index > 0 && !device.isSmall && (
                  <UI.Div sc={{ background: 'gray.200' }} />
                )}
                <UI.Div>
                  <UI.GridContainer
                    sc={{
                      columns: ['24px 1fr', '1fr'],
                      fontWeight: 500,
                      textAlign: ['left', 'center'],
                      gutter: [0.75, 0.5],
                      alignVertical: 'center',
                    }}
                  >
                    <UI.Div>
                      <UI.Badge sc={{ size: 30, background: 'secondary.100' }} style={{ margin: '0 auto' }}>
                        <UI.Icon sc={{ size: 1.5, color: 'secondary' }}>
                          <Check strokeWidth={1.5} />
                        </UI.Icon>
                      </UI.Badge>
                    </UI.Div>
                    <UI.Div style={{ textWrap: 'balance' }}>
                      {usp}
                    </UI.Div>
                  </UI.GridContainer>
                </UI.Div>
              </Fragment>
            ))}
          </UI.GridContainer>
        </UI.Card>
      )}

      {event.description && (
        <UI.HTML html={event.description} />
      )}

      {!event.description && (
        <UI.Div sc={{ color: 'gray.600' }}>
          {t('event_info.no_description_yet')}
        </UI.Div>
      )}

      {event.enable_fundraising && event.fundraising_campaigns_count > 0 && (
        <>
          <UI.HR />

          <FundraisingSection />
        </>
      )}

      <UI.HR />

      <OrganizerSection />

      {event.related_events.length > 0 && (
        <>
          <UI.HR />

          <UI.GridContainer sc={{ gutter: 0.75 }}>
            <UI.H3 sc={{ color: 'gray.600', fontWeight: 500 }}>
              {t('event_info.related_events')}
            </UI.H3>

            <UI.GridContainer sc={{ gutter: 0.75 }}>
              {visibleEvents.map((relatedEvent) => (
                <UI.LinkBlock to={`/${relatedEvent.id}`} title={relatedEvent.title} key={relatedEvent.id}>
                  <UI.GridContainer sc={{ columns: '72px 1fr', gutter: 0.75, alignVertical: 'center' }}>
                    <EventHeader
                      headerUrl={relatedEvent.header?.file.url}
                      size={900}
                      sportType={relatedEvent.ticket_categories.filter(
                        (ticketCategory) => ticketCategory.sport_type,
                      )[0]?.sport_type}
                      aspectRatio={1.25}
                      sc={{ padding: 0, borderRadius: 'xl' }}
                      color={relatedEvent.brand_color || defaultSecondary}
                      alt={relatedEvent.title}
                    />
                    <UI.Div style={{ minWidth: 0 }}>
                      <UI.H5 sc={{ fontWeight: 600 }}>
                        <UI.Truncate>
                          {relatedEvent.title}
                        </UI.Truncate>
                      </UI.H5>
                      <EventInfo
                        event={{ ...relatedEvent, location: { ...relatedEvent.location, id: null } }}
                        direction="column"
                      />
                    </UI.Div>
                  </UI.GridContainer>
                </UI.LinkBlock>
              ))}
            </UI.GridContainer>

            {visibleEvents.length < event.related_events.length && (
              <UI.FlexContainer sc={{ justifyContent: 'center' }}>
                <UI.Button
                  onClick={() => setEventsLimit((limit) => limit + eventsPerPage)}
                  sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
                >
                  <UI.Icon>
                    <ArrowDown />
                  </UI.Icon>
                  {' '}
                  {t('event_info.show_more')}
                </UI.Button>
              </UI.FlexContainer>
            )}

            {visibleEvents.length > eventsPerPage && visibleEvents.length >= event.related_events.length && (
              <UI.FlexContainer sc={{ justifyContent: 'center' }}>
                <UI.Button
                  onClick={() => setEventsLimit(eventsPerPage)}
                  sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
                >
                  <UI.Icon>
                    <ArrowUp />
                  </UI.Icon>
                  {' '}
                  {t('event_info.show_less')}
                </UI.Button>
              </UI.FlexContainer>
            )}
          </UI.GridContainer>
        </>
      )}
    </>
  );
};

export const FundraisingSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { locale, formatNumber, formatCurrency } = useLocale();

  const supportaLocale = config.supporta.supportedLocales.includes(locale) ? locale : Locale.en;

  const exclusiveCharity = event.charities.length === 1 && !event.allow_other_charity ? event.charities[0] : null;

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3 sc={{ fontSize: 3, color: 'gray.600', fontWeight: 500 }}>
        {t('event_info.fundraising')}
      </UI.H3>

      <UI.NavCard
        href={`${config.supporta.url}/${supportaLocale}/fundraisers/all/${event.id}`}
        target="_blank"
        rel="noopener noreferrer"
        sc={{ spacing: 'sm', background: 'gray.150/80', outline: 'gray.200' }}
        title={t('common:supporta')}
      >
        <UI.GridContainer
          sc={{ columns: '40px min-content min-content 1fr 18px', gutter: [0.75, 1], alignVertical: 'center' }}
        >
          <UI.Div>
            <UI.ProfilePic
              sc={{
                size: 40,
                background: !exclusiveCharity?.logo_url ? 'supporta_emerald' : 'white',
                color: 'white',
              }}
            >
              {exclusiveCharity?.logo_url ? (
                <img src={exclusiveCharity.logo_url} alt={exclusiveCharity.title} />
              ) : (
                <UI.Icon sc={{ size: 1.25 }}>
                  <SupportaSymbol />
                </UI.Icon>
              )}
            </UI.ProfilePic>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
              {formatCurrency(event.funds_raised, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </UI.Div>
            <UI.Div sc={{ small: true }}>
              {t('event_info.raised')}
            </UI.Div>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
              {formatNumber(event.fundraising_campaigns_count)}
            </UI.Div>
            <UI.Div sc={{ small: true }}>
              {t('event_info.fundraisers', { count: event.fundraising_campaigns_count })}
            </UI.Div>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            {!exclusiveCharity && (
              <>
                <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
                  {formatNumber(event.fundraising_charities_count)}
                </UI.Div>
                <UI.Div sc={{ small: true }}>
                  {t('event_info.charities', { count: event.fundraising_charities_count })}
                </UI.Div>
              </>
            )}
          </UI.Div>
          <UI.Div>
            <UI.Icon sc={{ size: 1.25, color: 'gray.500' }}>
              <ExternalLink />
            </UI.Icon>
          </UI.Div>
        </UI.GridContainer>
      </UI.NavCard>
    </UI.GridContainer>
  );
};

export const OrganizerSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { project } = event;

  const email = event.email || project.organisation_email;

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3 sc={{ fontSize: 3, color: 'gray.600', fontWeight: 500 }}>
        {t('organizer_info.organizer')}
      </UI.H3>

      <UI.GridContainer
        sc={{ columns: project.logo ? '42px 1fr' : '1fr', gutter: 0.5, alignVertical: 'center' }}
      >
        {project.logo && (
          <UI.ProfilePic sc={{ background: 'white', size: 42, borderRadius: 'md' }}>
            <img src={resizedImageUrl(project.logo.url, 300, 'webp')} alt={project.organisation_name} />
          </UI.ProfilePic>
        )}
        <UI.Div>
          <UI.H4>
            {project.organisation_name}
          </UI.H4>
          {event.website_url && (
            <UI.Div>
              <UI.A href={event.website_url} target="_blank" rel="noopener noreferrer" sc={{ secondary: true }}>
                {getUrlWithoutPrefix(event.website_url).split('/')[0]}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Div>
          )}
          {!event.website_url && email && (
            <UI.Div>
              <UI.A href={`mailto:${email}`} sc={{ secondary: true }}>
                {email}
              </UI.A>
            </UI.Div>
          )}
        </UI.Div>
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

const RegisterButton = styled(UI.LinkButton)`
  ${({ theme }) => css`
    box-shadow: 0 3px 15px ${theme.transparentize(theme.colors.secondary[500], 0.5)}, inset 0 0 1px 1px ${theme.transparentize(theme.colors.secondary[400], 0.7)};
    backdrop-filter: blur(3px);
  `}
`;

export default AboutPage;
